import React from 'react';
import { Box, Divider, IconButton, Tooltip, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';

const useStyles = makeStyles((theme) => ({
  panel: {
    background: 'rgba(10, 14, 30, 0.88)',
    border: '1px solid rgba(77, 121, 255, 0.22)',
    borderRadius: 14,
    padding: 14,
    textAlign: 'left',
    boxSizing: 'border-box',
    [theme.breakpoints.down('sm')]: {
      padding: 12,
    },
  },
  layout: {
    display: 'grid',
    gridTemplateColumns: 'minmax(200px, 260px) minmax(0, 1fr)',
    gap: 12,
    alignItems: 'start',
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
    },
  },
  title: {
    fontSize: 12,
    fontWeight: 900,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    color: '#9ea4d8',
    marginBottom: 10,
  },
  divider: {
    background: 'rgba(255,255,255,0.08)',
    marginBottom: 12,
  },
  scoreHero: {
    borderRadius: 12,
    border: '1px solid rgba(77, 121, 255, 0.18)',
    background: 'linear-gradient(180deg, rgba(20, 28, 55, 0.92), rgba(8, 12, 24, 0.92))',
    padding: '12px 12px 10px',
  },
  scoreLabelRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  scoreLabel: {
    color: '#9ea4d8',
    fontSize: 11,
    fontWeight: 800,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    marginBottom: 8,
  },
  infoButton: {
    color: '#cfd6ff',
    padding: 2,
    marginTop: -6,
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,0.06)',
    },
  },
  scoreValue: {
    color: '#e5e7ff',
    fontSize: 26,
    fontWeight: 900,
    lineHeight: 1.1,
  },
  scoreBand: {
    color: '#cfd6ff',
    fontSize: 12,
    fontWeight: 700,
    marginTop: 6,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    gap: 10,
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
    },
  },
  row: {
    borderRadius: 12,
    border: '1px solid rgba(77, 121, 255, 0.18)',
    background: 'rgba(12, 18, 38, 0.85)',
    padding: '10px 12px',
  },
  rowTop: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 10,
    alignItems: 'center',
    marginBottom: 8,
  },
  rowLabel: {
    color: '#e5e7ff',
    fontSize: 13,
    fontWeight: 800,
  },
  rowValue: {
    color: '#9ef7c5',
    fontSize: 13,
    fontWeight: 800,
  },
  barTrack: {
    width: '100%',
    height: 8,
    borderRadius: 999,
    background: 'rgba(255,255,255,0.08)',
    overflow: 'hidden',
  },
  barFill: {
    height: '100%',
    borderRadius: 999,
    background: 'linear-gradient(90deg, rgba(77,121,255,0.95), rgba(158,247,197,0.95))',
  },
  rowMeta: {
    color: '#8d93c9',
    fontSize: 11,
    lineHeight: 1.35,
    marginTop: 8,
  },
  footer: {
    color: '#9ea4d8',
    fontSize: 12,
    lineHeight: 1.45,
    marginTop: 12,
  },
  error: {
    color: '#ffb4b4',
    fontSize: 12,
    lineHeight: 1.45,
    marginTop: 8,
  },
}));

function toNum(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

export default function TokenScorePanel({ intelligence, scoreData, loading, error }) {
  const classes = useStyles();
  const resolvedScoreData = scoreData || (intelligence && intelligence.score) || null;
  const score = resolvedScoreData && Number.isFinite(Number(resolvedScoreData.score)) ? Number(resolvedScoreData.score) : 0;
  const components = resolvedScoreData && resolvedScoreData.components ? resolvedScoreData.components : {};
  const explanation = resolvedScoreData && resolvedScoreData.explanation ? resolvedScoreData.explanation : {};
  const rows = [
    { key: 'liquidityDepth', label: 'Liquidity Quality', value: toNum(components.liquidityDepth), meta: explanation.liquidityDepth || 'No liquidity explanation yet' },
    { key: 'walletDistribution', label: 'Wallet Distribution', value: toNum(components.walletDistribution), meta: explanation.walletDistribution || 'No distribution explanation yet' },
    { key: 'activityLevel', label: 'Activity Level', value: toNum(components.activityLevel), meta: explanation.activityLevel || 'No activity explanation yet' },
    { key: 'narrativeStrength', label: 'Narrative Strength', value: toNum(components.narrativeStrength), meta: explanation.narrativeStrength || 'No narrative explanation yet' },
  ];

  return (
    <Box className={classes.panel}>
      <Box className={classes.layout}>
        <Box className={classes.scoreHero}>
          <Box className={classes.scoreLabelRow}>
            <Typography className={classes.scoreLabel}>Token Intelligence Score</Typography>
            <Tooltip
              title="Composite score (0-100) derived from liquidity quality, activity signals, and narrative coverage. Use it as a fast triage signal rather than a guarantee."
              placement="top"
            >
              <IconButton size="small" className={classes.infoButton} aria-label="About Token Intelligence Score">
                <InfoOutlinedIcon style={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Box>
          <Typography className={classes.scoreValue}>{score}/100</Typography>
        </Box>

        <Box className={classes.grid}>
          {rows.map((row) => (
            <Box key={row.key} className={classes.row}>
              <Box className={classes.rowTop}>
                <Typography className={classes.rowLabel}>{row.label}</Typography>
                <Typography className={classes.rowValue}>{row.value}/100</Typography>
              </Box>
              <Box className={classes.barTrack}>
                <Box className={classes.barFill} style={{ width: `${Math.max(0, Math.min(100, row.value))}%` }} />
              </Box>
              <Typography className={classes.rowMeta}>{row.meta}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
      {error ? <Typography className={classes.error}>{error}</Typography> : null}
    </Box>
  );
}
